import React, { useState, useMemo, useCallback } from 'react';
import {
  Card, Descriptions, Tag, Button, Space, Typography, Input, Select,
  message, Modal, Result, Divider, Timeline, Empty, Alert, Form,
  Drawer, Radio, Image,
} from 'antd';
import {
  ArrowLeftOutlined, ClockCircleOutlined, SyncOutlined,
  CheckCircleOutlined, MinusCircleOutlined,
  TeamOutlined, TagOutlined, ExclamationCircleOutlined,
  SendOutlined, UndoOutlined, EditOutlined, DeleteOutlined,
  UserSwitchOutlined, LinkOutlined, PlusOutlined,
  FileImageOutlined, FileZipOutlined, FileTextOutlined,
  FileExcelOutlined, FilePdfOutlined,
  FileOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import TapdRichEditor from './TapdRichEditor';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// ══════════════════════════════════════════════
//  常量映射
// ══════════════════════════════════════════════

const STATUS_MAP = {
  pending:    { label: '待处理',   color: 'blue',    icon: <ClockCircleOutlined /> },
  processing: { label: '处理中',   color: 'orange',  icon: <SyncOutlined spin /> },
  resolved:   { label: '已解决',   color: 'green',   icon: <CheckCircleOutlined /> },
  closed:     { label: '已关闭',   color: 'default', icon: <MinusCircleOutlined /> },
};

const PRIORITY_MAP = {
  low:      { label: '低',   color: 'default' },
  normal:   { label: '普通', color: 'blue' },
  high:     { label: '高',   color: 'orange' },
  urgent:   { label: '紧急', color: 'red' },
};

const CATEGORY_LABEL = {
  techSupport: '技术对接',
  bug: 'BUG',
  requirement: '需求',
  other: '其他',
};

// 问题类型（当前已知枚举，平台管理员可在任意工单状态修改）
const ISSUE_TYPE_OPTIONS = [
  'PAAS平台', 'SAAS平台', '生态平台', '安卓app', 'IOS APP',
  '固件问题', '固件SDK', '安卓SDK', 'IOS SDK', '设置问题',
];

// 所属App展示格式：App应用名称-系统-区域
// TODO: 联调时替换为接口数据
const APP_DISPLAY_MAP = {
  'app_qx-iOS-GL': '牵心Pro-iOS-全球',
  'app_qx-Android-GL': '牵心Pro-Android-全球',
  'app_hs-iOS-CN': 'HomeStar-iOS-国内',
  'app_hs-Android-CN': 'HomeStar-Android-国内',
  'app_hs-iOS-OV': 'HomeStar-iOS-海外',
};

// 附件类型映射：图标 + 主题色（用于详情页附件展示）
const FILE_TYPE_MAP = {
  image: { icon: <FileImageOutlined />, color: '#1677ff' },
  zip:   { icon: <FileZipOutlined />, color: '#faad14' },
  txt:   { icon: <FileTextOutlined />, color: '#8c8c8c' },
  xlsx:  { icon: <FileExcelOutlined />, color: '#52c41a' },
  pdf:   { icon: <FilePdfOutlined />, color: '#ff4d4f' },
  file:  { icon: <FileOutlined />, color: '#8c8c8c' },
};

const REMARK_TYPE_MAP = {
  enterprise: { label: '企业', color: 'blue' },
  staff:      { label: '客服', color: 'green' },
  system:     { label: '系统', color: 'default' },
};

// TAPD 单状态映射（tapd.status）
const TAPD_STATUS_MAP = {
  developing: { label: '开发中', color: 'blue' },
  testing:    { label: '测试中', color: 'purple' },
  closed:     { label: '已关闭', color: 'default' },
};

// 创建TAPD抽屉：表单选项（TODO: 联调时替换为接口数据）
const TAPD_TYPE_OPTIONS = [
  { value: 'story', label: '需求' },
  { value: 'bug',   label: '缺陷' },
];

const TAPD_SPACES = [
  { value: '32860459', label: '鹤梦云-IoT平台研发空间（32860459）' },
  { value: '39258384', label: '鹤梦云-APP研发空间（39258384）' },
  { value: '29861651', label: '鹤梦云-固件研发空间（29861651）' },
];

const TAPD_STORY_CATEGORIES = ['产品需求', '运营需求', '技术需求', '其他需求'];
const TAPD_STORY_PRIORITIES = ['低', '中', '高', '紧急'];
const TAPD_BUG_SOURCES = ['测试发现', '用户反馈', '客服转单', '内部评审'];
const TAPD_BUG_PRIORITIES = ['低', '中', '高', '紧急'];
const TAPD_BUG_SEVERITIES = ['致命', '严重', '一般', '轻微'];

// 生成 TAPD 单跳转链接：https://www.tapd.cn/tapd_fe/{空间ID}/{story|bug}/detail/{单号}
const buildTapdUrl = (tapd) =>
  `https://www.tapd.cn/tapd_fe/${tapd.workspaceId}/${tapd.type === 'bug' ? 'bug' : 'story'}/detail/${tapd.tapdId}`;

// 构建创建 TAPD 时默认写入的富文本内容：工单描述 + 工单附件（图片嵌入 / 文件转链接）
const buildInitialTapdContent = (ticket) => {
  const parts = [];
  if (ticket.description) {
    parts.push(`<p>${ticket.description.replace(/\n/g, '<br/>')}</p>`);
  }
  const attachments = ticket.attachments || [];
  if (attachments.length > 0) {
    parts.push('<p><strong>附件：</strong></p>');
    attachments.forEach((f) => {
      if (f.type === 'image') {
        parts.push(`<p><img src="${f.url}" alt="${f.name}" style="max-width:100%;" /></p>`);
      } else {
        parts.push(`<p><a href="${f.url || '#'}" target="_blank">📎 ${f.name}</a></p>`);
      }
    });
  }
  return parts.join('');
};

// ══════════════════════════════════════════════
//  Mock 数据
//  TODO: 联调时替换为接口数据
// ══════════════════════════════════════════════

// 工单 Mock（按 ticketNo 索引）
const MOCK_TICKETS = {
  'TK202607270001': {
    ticketNo: 'TK202607270001', title: 'test-SDK初始化接口返回错误码1001',
    enterpriseId: 'test-ent-001', enterpriseName: 'test-深圳智创科技有限公司',
    category: 'techSupport', issueType: 'IOS SDK', priority: 'urgent', status: 'pending',
    appValue: 'app_qx-iOS-GL', projectName: 'SDK联调项目',
    handlerId: null, handlerName: null,
    description: '调用SDK初始化接口时返回错误码1001，环境为iOS 18，SDK版本v3.1.0。已尝试重新集成SDK、清理缓存，问题依然存在。',
    attachments: [
      { name: '错误日志.txt', type: 'txt', size: '86KB', url: '#' },
      { name: 'SDK版本信息.png', type: 'image', size: '256KB', url: 'https://picsum.photos/seed/hm-sdk/400/300' },
    ],
    tapd: null, // 未创建 TAPD 单
    createdAt: '2026-07-27 09:15:00', updatedAt: '2026-07-27 09:15:00',
  },
  'TK202607270002': {
    ticketNo: 'TK202607270002', title: 'test-License采购量统计对不上',
    enterpriseId: 'test-ent-002', enterpriseName: 'test-广州迅捷科技有限公司',
    category: 'techSupport', issueType: 'SAAS平台', priority: 'high', status: 'processing',
    appValue: 'app_hs-iOS-CN', projectName: '私有化部署',
    handlerId: 'user-01', handlerName: '李客服',
    description: '后台显示的License采购数量与实际下单数量不一致，少了20个。订单号为PO20260725001。',
    attachments: [
      { name: '采购订单截图.png', type: 'image', size: '1.2MB', url: 'https://picsum.photos/seed/hm-order/400/300' },
    ],
    tapd: { workspaceId: '32860459', workspaceName: '鹤梦云-IoT平台研发空间（32860459）', type: 'bug', tapdId: '1139258384001035530', title: 'test-License采购量统计对不上', status: 'developing' },
    createdAt: '2026-07-27 08:30:00', updatedAt: '2026-07-27 10:00:00',
  },
  'TK202607270003': {
    ticketNo: 'TK202607270003', title: 'test-设备固件OTA升级失败',
    enterpriseId: 'test-ent-003', enterpriseName: 'test-上海云联科技有限公司',
    category: 'bug', issueType: '固件问题', priority: 'high', status: 'pending',
    appValue: '', projectName: '',
    handlerId: null, handlerName: null,
    description: '设备型号X200，固件版本v2.3→v2.5升级过程中设备重启后变砖，无法正常启动。',
    attachments: [
      { name: '设备变砖现场照片.png', type: 'image', size: '2.4MB', url: 'https://picsum.photos/seed/hm-brick/400/300' },
      { name: 'OTA升级日志.zip', type: 'zip', size: '5.8MB', url: '#' },
    ],
    createdAt: '2026-07-27 07:45:00', updatedAt: '2026-07-27 07:45:00',
  },
  'TK202607260004': {
    ticketNo: 'TK202607260004', title: 'test-App OEM定制需要新增品牌配置页',
    enterpriseId: 'test-ent-001', enterpriseName: 'test-深圳智创科技有限公司',
    category: 'requirement', issueType: '安卓app', priority: 'normal', status: 'resolved',
    appValue: 'app_qx-Android-GL', projectName: 'SDK联调项目',
    handlerId: 'user-02', handlerName: '王客服',
    description: '希望在App OEM定制中增加一个品牌配置页面，用于设置Logo和主题色。',
    attachments: [
      { name: '品牌配置页需求原型.zip', type: 'zip', size: '12.6MB', url: '#' },
    ],
    tapd: { workspaceId: '32860459', workspaceName: '鹤梦云-IoT平台研发空间（32860459）', type: 'story', tapdId: '1132860459001101556', title: 'test-App OEM定制需要新增品牌配置页', status: 'testing' },
    createdAt: '2026-07-26 16:00:00', updatedAt: '2026-07-27 11:00:00',
  },
  'TK202607250005': {
    ticketNo: 'TK202607250005', title: 'test-云平台私有化部署环境确认',
    enterpriseId: 'test-ent-005', enterpriseName: 'test-北京数联科技有限公司',
    category: 'techSupport', issueType: 'PAAS平台', priority: 'normal', status: 'closed',
    appValue: '', projectName: '私有化部署',
    handlerId: 'user-03', handlerName: '张客服',
    description: '确认私有化部署的服务器配置和环境要求。',
    attachments: [
      { name: '服务器配置清单.xlsx', type: 'xlsx', size: '48KB', url: '#' },
    ],
    tapd: { workspaceId: '39258384', workspaceName: '鹤梦云-APP研发空间（39258384）', type: 'story', tapdId: '1132860459001101024', title: 'test-云平台私有化部署环境确认', status: 'closed' },
    createdAt: '2026-07-25 14:20:00', updatedAt: '2026-07-26 09:00:00',
  },
  'TK202607270006': {
    ticketNo: 'TK202607270006', title: 'test-平台API对接认证失败',
    enterpriseId: 'test-ent-004', enterpriseName: 'test-成都天视科技有限公司',
    category: 'techSupport', issueType: '生态平台', priority: 'normal', status: 'pending',
    appValue: 'app_hs-iOS-OV', projectName: '',
    handlerId: null, handlerName: null,
    description: '调用平台API时token认证一直返回401，确认过appKey和appSecret无误。',
    attachments: [
      { name: 'API请求日志.txt', type: 'txt', size: '32KB', url: '#' },
      { name: '认证失败截图.png', type: 'image', size: '380KB', url: 'https://picsum.photos/seed/hm-auth/400/300' },
    ],
    createdAt: '2026-07-27 10:30:00', updatedAt: '2026-07-27 10:30:00',
  },
  'TK202607270007': {
    ticketNo: 'TK202607270007', title: 'test-批量设备管理需求',
    enterpriseId: 'test-ent-006', enterpriseName: 'test-杭州联智科技有限公司',
    category: 'requirement', issueType: '', priority: 'low', status: 'processing',
    appValue: 'app_hs-Android-CN', projectName: '已归档项目',
    handlerId: 'user-01', handlerName: '李客服',
    description: '需要支持一次性导入1000+设备的批量管理功能。',
    attachments: [
      { name: '批量设备管理需求说明.pdf', type: 'pdf', size: '860KB', url: '#' },
    ],
    tapd: null, // 已领取但尚未创建 TAPD 单（演示创建流程）
    createdAt: '2026-07-27 11:00:00', updatedAt: '2026-07-27 13:00:00',
  },
  'TK202607240008': {
    ticketNo: 'TK202607240008', title: 'test-套餐服务配置咨询',
    enterpriseId: 'test-ent-002', enterpriseName: 'test-广州迅捷科技有限公司',
    category: 'other', issueType: '设置问题', priority: 'low', status: 'resolved',
    appValue: '', projectName: '',
    handlerId: 'user-02', handlerName: '王客服',
    description: '咨询如何配置设备的增值服务套餐。',
    attachments: [],
    createdAt: '2026-07-24 13:00:00', updatedAt: '2026-07-25 16:00:00',
  },
};

// CRM Mock（按 enterpriseId 索引）
const MOCK_CRM = {
  'test-ent-001': {
    enterpriseName: 'test-深圳智创科技有限公司', enterpriseId: 'test-ent-001',
    contactPerson: '张三', contactPhone: '13800001111',
    responsibleName: '李客服', riskNotes: '客户项目上线日期临近，SDK问题可能影响交付',
    tags: { customerCategory: ['私有定制'], capability: ['方案商', '应用服务商'], customerTag: ['01战略客户'] },
  },
  'test-ent-002': {
    enterpriseName: 'test-广州迅捷科技有限公司', enterpriseId: 'test-ent-002',
    contactPerson: '李四', contactPhone: '13800002222',
    responsibleName: '王客服', riskNotes: '',
    tags: { customerCategory: ['公版定制'], capability: ['工厂'], customerTag: ['02大客户'] },
  },
  'test-ent-003': {
    enterpriseName: 'test-上海云联科技有限公司', enterpriseId: 'test-ent-003',
    contactPerson: '王五', contactPhone: '13800003333',
    responsibleName: '李客服', riskNotes: '',
    tags: { customerCategory: ['私有定制'], capability: ['应用服务商'], customerTag: ['02大客户'] },
  },
  'test-ent-004': {
    enterpriseName: 'test-成都天视科技有限公司', enterpriseId: 'test-ent-004',
    contactPerson: '赵六', contactPhone: '13800004444',
    responsibleName: '张客服', riskNotes: '',
    tags: { customerCategory: ['标准对接'], capability: ['渠道商'], customerTag: ['03中等客户'] },
  },
  'test-ent-005': {
    enterpriseName: 'test-北京数联科技有限公司', enterpriseId: 'test-ent-005',
    contactPerson: '孙七', contactPhone: '13800005555',
    responsibleName: '王客服', riskNotes: '',
    tags: { customerCategory: ['标准对接'], capability: ['方案商'], customerTag: ['03中等客户'] },
  },
};

// 备注 Mock（按 ticketNo 索引）
const MOCK_REMARKS = {
  'TK202607270001': [
    { id: '1', type: 'enterprise', author: '张三（企业）', content: '调用SDK初始化接口时返回错误码1001，环境为iOS 18...', createdAt: '2026-07-27 09:15:00' },
  ],
  'TK202607270002': [
    { id: '1', type: 'enterprise', author: '李四（企业）', content: '后台显示的License采购数量与实际下单数量不一致，少了20个。', createdAt: '2026-07-27 08:30:00' },
    { id: '2', type: 'system', author: '系统', content: '李客服 领取了工单', createdAt: '2026-07-27 09:00:00' },
    { id: '3', type: 'staff', author: '李客服', content: '已排查，是订单系统同步延迟导致，正在手动补推数据。预计今天下午前完成。', createdAt: '2026-07-27 10:00:00' },
  ],
  'TK202607270003': [
    { id: '1', type: 'enterprise', author: '王五（企业）', content: '设备型号X200，固件版本v2.3→v2.5升级过程中设备重启后变砖。', createdAt: '2026-07-27 07:45:00' },
  ],
  'TK202607260004': [
    { id: '1', type: 'enterprise', author: '张三（企业）', content: '希望在App OEM定制中增加一个品牌配置页面。', createdAt: '2026-07-26 16:00:00' },
    { id: '2', type: 'system', author: '系统', content: '王客服 领取了工单', createdAt: '2026-07-26 17:00:00' },
    { id: '3', type: 'staff', author: '王客服', content: '已评估需求，品牌配置页已加入下个迭代计划，预计下周三上线。', createdAt: '2026-07-27 10:00:00' },
    { id: '4', type: 'system', author: '系统', content: '王客服 将状态从〔处理中〕变为〔已解决〕', createdAt: '2026-07-27 11:00:00' },
  ],
  'TK202607250005': [
    { id: '1', type: 'enterprise', author: '孙七（企业）', content: '确认私有化部署的服务器配置和环境要求。', createdAt: '2026-07-25 14:20:00' },
    { id: '2', type: 'system', author: '系统', content: '张客服 领取了工单', createdAt: '2026-07-25 15:00:00' },
    { id: '3', type: 'staff', author: '张客服', content: '已发送部署文档和环境要求清单，请查收。', createdAt: '2026-07-25 17:00:00' },
    { id: '4', type: 'system', author: '系统', content: '张客服 将状态从〔处理中〕变为〔已解决〕', createdAt: '2026-07-26 08:00:00' },
    { id: '5', type: 'system', author: '系统', content: '张客服 关闭了工单', createdAt: '2026-07-26 09:00:00' },
  ],
  'TK202607270006': [
    { id: '1', type: 'enterprise', author: '赵六（企业）', content: '调用平台API时token认证一直返回401。', createdAt: '2026-07-27 10:30:00' },
  ],
  'TK202607270007': [
    { id: '1', type: 'enterprise', author: '周八（企业）', content: '需要支持一次性导入1000+设备的批量管理功能。', createdAt: '2026-07-27 11:00:00' },
    { id: '2', type: 'system', author: '系统', content: '李客服 领取了工单', createdAt: '2026-07-27 11:30:00' },
  ],
  'TK202607240008': [
    { id: '1', type: 'enterprise', author: '李四（企业）', content: '咨询如何配置设备的增值服务套餐。', createdAt: '2026-07-24 13:00:00' },
    { id: '2', type: 'system', author: '系统', content: '王客服 领取了工单', createdAt: '2026-07-24 14:00:00' },
    { id: '3', type: 'staff', author: '王客服', content: '已提供套餐配置指引文档和视频教程链接。', createdAt: '2026-07-25 15:00:00' },
    { id: '4', type: 'system', author: '系统', content: '王客服 将状态从〔处理中〕变为〔已解决〕', createdAt: '2026-07-25 16:00:00' },
  ],
};

const CURRENT_AGENT = { id: 'user-99', name: '当前客服' };

// ══════════════════════════════════════════════
//  Mock 客服账号（供分配选择）
//  TODO: 联调时替换为接口数据
// ══════════════════════════════════════════════
const MOCK_AGENTS = [
  { id: 'user-01', name: '李客服', account: 'lifuwu' },
  { id: 'user-02', name: '王客服', account: 'wangyuan' },
  { id: 'user-03', name: '张客服', account: 'zhangwei' },
  { id: 'user-04', name: '赵客服', account: 'zhaoyun' },
  { id: 'user-05', name: '陈客服', account: 'chenli' },
];

// ══════════════════════════════════════════════
//  主组件
// ══════════════════════════════════════════════

function TicketDetail() {
  const { ticketNo } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(() => MOCK_TICKETS[ticketNo] || null);
  const [remarks, setRemarks] = useState(() => MOCK_REMARKS[ticketNo] || []);
  const [remarkText, setRemarkText] = useState('');
  const [savingRemark, setSavingRemark] = useState(false);
  const [editing, setEditing] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [editForm] = Form.useForm();
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [tapdDrawerOpen, setTapdDrawerOpen] = useState(false);
  const [tapdForm] = Form.useForm();
  const [tapdExtraAttachments, setTapdExtraAttachments] = useState([]);
  const tapdType = Form.useWatch('type', tapdForm);
  const tapdSpace = Form.useWatch('space', tapdForm);

  const crm = useMemo(() => MOCK_CRM[ticket?.enterpriseId] || null, [ticket]);

  // ==================== 备注添加 ====================
  const handleAddRemark = useCallback(() => {
    if (!remarkText.trim()) {
      message.warning('请输入回复内容');
      return;
    }
    setSavingRemark(true);
    setTimeout(() => {
      const newRemark = {
        id: Date.now().toString(),
        type: 'staff',
        author: CURRENT_AGENT.name,
        content: remarkText.trim(),
        createdAt: new Date().toISOString().replace('T', ' ').slice(0, 19),
      };
      setRemarks(prev => [newRemark, ...prev]);
      setRemarkText('');
      setSavingRemark(false);
      message.success('回复已添加');
    }, 300);
  }, [remarkText]);

  // ==================== 状态操作 ====================
  const updateStatus = useCallback((newStatus, systemMsg) => {
    const now = new Date().toISOString().replace('T', ' ').slice(0, 19);
    setTicket(prev => ({
      ...prev,
      status: newStatus,
      handlerId: newStatus === 'pending' ? null : (prev.handlerId || CURRENT_AGENT.id),
      handlerName: newStatus === 'pending' ? null : (prev.handlerName || CURRENT_AGENT.name),
      updatedAt: now,
    }));
    const sysRemark = {
      id: Date.now().toString(), type: 'system', author: '系统',
      content: systemMsg, createdAt: now,
    };
    setRemarks(prev => [sysRemark, ...prev]);
  }, []);

  const handleMarkResolved = useCallback(() => {
    if (remarks.filter(r => r.type === 'staff').length === 0) {
      message.warning('请先添加回复');
      return;
    }
    Modal.confirm({
      title: '确认标记为已解决？',
      onOk: () => {
        updateStatus('resolved', `${CURRENT_AGENT.name} 将状态从〔处理中〕变为〔已解决〕`);
        message.success('已标记为已解决');
      },
    });
  }, [remarks, updateStatus]);

  const handleRelease = useCallback(() => {
    Modal.confirm({
      title: '确认释放该工单？释放后其他客服可重新领取',
      onOk: () => {
        updateStatus('pending', `${CURRENT_AGENT.name} 释放了工单`);
        message.success('工单已释放，退回待处理池');
      },
    });
  }, [updateStatus]);

  const handleClose = useCallback(() => {
    Modal.confirm({
      title: '确认关闭该工单？关闭后不可重新打开',
      onOk: () => {
        updateStatus('closed', `${CURRENT_AGENT.name} 关闭了工单`);
        message.success('工单已关闭');
      },
    });
  }, [updateStatus]);

  const handlePriorityChange = useCallback((newPriority) => {
    setTicket(prev => ({ ...prev, priority: newPriority, updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19) }));
    message.success('优先级已更新');
  }, []);

  // 问题类型：任意工单状态，平台管理员均可修改
  const handleIssueTypeChange = useCallback((newIssueType) => {
    setTicket(prev => ({ ...prev, issueType: newIssueType, updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19) }));
    message.success('问题类型已更新');
  }, []);

  const handleClaim = useCallback(() => {
    Modal.confirm({
      title: '确认领取该工单？',
      onOk: () => {
        updateStatus('processing', `${CURRENT_AGENT.name} 领取了工单`);
        message.success('已领取，工单状态更新为处理中');
      },
    });
  }, [updateStatus]);

  // ==================== 编辑 ====================
  const handleEdit = useCallback(() => {
    editForm.setFieldsValue({
      title: ticket.title,
      category: ticket.category,
      issueType: ticket.issueType,
      priority: ticket.priority,
      description: ticket.description,
    });
    setEditing(true);
  }, [ticket, editForm]);

  const handleSaveEdit = useCallback(async () => {
    try {
      const values = await editForm.validateFields();
      const now = new Date().toISOString().replace('T', ' ').slice(0, 19);
      setTicket(prev => ({ ...prev, ...values, updatedAt: now }));
      setEditing(false);
      message.success('保存成功');
    } catch {
      // 表单校验失败
    }
  }, [editForm]);

  const handleCancelEdit = useCallback(() => {
    setEditing(false);
  }, []);

  // ==================== 删除 ====================
  const handleDelete = useCallback(() => {
    Modal.confirm({
      title: '确认删除',
      content: '确认删除该工单？删除后不可恢复',
      okText: '确认删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => {
        setIsDeleted(true);
        message.success('工单已删除');
        setTimeout(() => navigate('/customer/ticket-manage'), 1000);
      },
    });
  }, [navigate]);

  // ==================== 分配 ====================
  const handleAssignOpen = useCallback(() => {
    setAssignTarget(null);
    setAssignModalVisible(true);
  }, []);

  const handleAssignConfirm = useCallback(() => {
    if (!assignTarget) {
      message.warning('请选择目标客服');
      return;
    }
    const target = MOCK_AGENTS.find(a => a.id === assignTarget);
    updateStatus('processing', `${CURRENT_AGENT.name} 将工单分配给客服 ${target?.name || assignTarget}`);
    setAssignModalVisible(false);
    setAssignTarget(null);
    message.success('已分配');
  }, [assignTarget, updateStatus]);

  // ==================== TAPD ====================
  const openTapdDrawer = useCallback(() => {
    tapdForm.resetFields();
    // 标题/内容默认回显工单标题与客户问题描述，并默认写入工单附件（图片嵌入 / 文件转链接）
    tapdForm.setFieldsValue({
      type: 'story',
      title: ticket.title,
      content: buildInitialTapdContent(ticket),
    });
    setTapdExtraAttachments([]);
    setTapdDrawerOpen(true);
  }, [tapdForm, ticket]);

  const handleCreateTapd = useCallback(async () => {
    try {
      const values = await tapdForm.validateFields();
      const space = TAPD_SPACES.find(s => s.value === values.space);
      const now = new Date().toISOString().replace('T', ' ').slice(0, 19);
      const tapd = {
        workspaceId: values.space,
        workspaceName: space?.label || values.space,
        type: values.type,
        tapdId: Date.now().toString(), // TODO: 联调时替换为 TAPD 创建成功后返回的真实单号
        title: values.title,
        content: values.content || '', // 富文本内容（HTML）
        // 附件随单创建：工单原有附件 + 富文本中上传的额外附件
        attachments: [...(ticket.attachments || []), ...tapdExtraAttachments],
        status: 'developing', // 新建 TAPD 单默认开发中
        createdAt: now,
      };
      setTicket(prev => ({ ...prev, tapd, updatedAt: now }));
      setTapdDrawerOpen(false);
      tapdForm.resetFields();
      message.success('TAPD 单已创建');
      // TODO: 联调时此处重新拉取工单详情接口，即「刷新工单详情页面」
    } catch {
      // 表单校验失败
    }
  }, [tapdForm, ticket.attachments, tapdExtraAttachments]);

  // ==================== 已删除/不存在处理 ====================
  if (isDeleted) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="info"
          title="工单已被删除"
          subTitle="该工单已被删除，无法查看详情"
          extra={<Button type="primary" onClick={() => navigate('/customer/ticket-manage')}>返回工单列表</Button>}
        />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div style={{ padding: 24 }}>
        <Result status="404" title="工单不存在"
          subTitle="工单不存在或已被删除"
          extra={<Button onClick={() => navigate('/customer/ticket-manage')}>返回工单列表</Button>} />
      </div>
    );
  }

  const statusInfo = STATUS_MAP[ticket.status];
  const priorityInfo = PRIORITY_MAP[ticket.priority];
  const isClosed = ticket.status === 'closed';
  const isPending = ticket.status === 'pending';
  const isProcessing = ticket.status === 'processing';
  const isResolved = ticket.status === 'resolved';

  return (
    <div style={{ padding: 24 }}>
      {/* 返回按钮 */}
      <Button icon={<ArrowLeftOutlined />} style={{ marginBottom: 16 }}
        onClick={() => navigate('/customer/ticket-manage')}>
        返回工单列表
      </Button>

      <div style={{ display: 'flex', gap: 16 }}>
        {/* ========== 左侧：工单信息 ========== */}
        <div style={{ flex: 1 }}>
          {editing ? (
            /* ========== 编辑模式 ========== */
            <Card bordered={false}
              style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0 }}>
                  <Text code style={{ fontSize: 14, marginRight: 8 }}>{ticket.ticketNo}</Text>
                  编辑工单
                </Title>
                <Tag color={statusInfo.color} icon={statusInfo.icon} style={{ fontSize: 14, padding: '2px 12px', border: 'none' }}>
                  {statusInfo.label}
                </Tag>
              </div>
              <Form form={editForm} layout="vertical" requiredMark={false}>
                <Form.Item name="title" label="工单标题"
                  rules={[{ required: true, message: '请填写工单标题' }, { max: 100, message: '标题不超过100字' }]}>
                  <Input placeholder="请输入工单标题" maxLength={100} showCount />
                </Form.Item>
                <Form.Item name="category" label="工单分类"
                  rules={[{ required: true, message: '请选择工单分类' }]}>
                  <Select style={{ width: 160 }}>
                    <Option value="techSupport">技术对接</Option>
                    <Option value="bug">BUG</Option>
                    <Option value="requirement">需求</Option>
                    <Option value="other">其他</Option>
                  </Select>
                </Form.Item>
                <Form.Item name="issueType" label="问题类型">
                  <Select style={{ width: 200 }} allowClear placeholder="请选择问题类型">
                    {ISSUE_TYPE_OPTIONS.map(t => <Option key={t} value={t}>{t}</Option>)}
                  </Select>
                </Form.Item>
                <Form.Item name="priority" label="优先级">
                  <Select style={{ width: 160 }}>
                    <Option value="low">低</Option>
                    <Option value="normal">普通</Option>
                    <Option value="high">高</Option>
                    <Option value="urgent">紧急</Option>
                  </Select>
                </Form.Item>
                <Form.Item name="description" label="问题描述"
                  rules={[{ required: true, message: '请填写问题描述' }, { min: 10, message: '问题描述不少于10字' }]}>
                  <TextArea rows={6} placeholder="请详细描述遇到的问题" maxLength={5000} showCount />
                </Form.Item>
                <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
                  <Button type="primary" onClick={handleSaveEdit}>保存</Button>
                  <Button onClick={handleCancelEdit}>取消</Button>
                </div>
              </Form>
            </Card>
          ) : (
            /* ========== 只读模式：基本信息卡片 ========== */
            <Card bordered={false}
              style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    <Text code style={{ fontSize: 14, marginRight: 8 }}>{ticket.ticketNo}</Text>
                    {ticket.title}
                  </Title>
                </div>
                <Space size={8}>
                  {isPending && (
                    <>
                      <Button size="small" icon={<EditOutlined />} onClick={handleEdit}>编辑</Button>
                      <Button size="small" danger icon={<DeleteOutlined />} onClick={handleDelete}>删除</Button>
                    </>
                  )}
                  <Tag color={statusInfo.color} icon={statusInfo.icon} style={{ fontSize: 14, padding: '2px 12px', border: 'none' }}>
                    {statusInfo.label}
                  </Tag>
                </Space>
              </div>

              <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
                <Descriptions.Item label="工单分类">
                  {isClosed || isResolved || isProcessing ? (
                    <Tag color="purple" style={{ border: 'none' }}>{CATEGORY_LABEL[ticket.category]}</Tag>
                  ) : (
                    <Select size="small" value={ticket.category} style={{ width: 120 }}
                      onChange={(val) => {
                        setTicket(prev => ({ ...prev, category: val, updatedAt: new Date().toISOString().replace('T', ' ').slice(0, 19) }));
                        message.success('工单分类已更新');
                      }}>
                      <Option value="techSupport">技术对接</Option>
                      <Option value="bug">BUG</Option>
                      <Option value="requirement">需求</Option>
                      <Option value="other">其他</Option>
                    </Select>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="问题类型">
                  <Select size="small" value={ticket.issueType} style={{ width: 140 }} allowClear placeholder="请选择"
                    onChange={handleIssueTypeChange}>
                    {ISSUE_TYPE_OPTIONS.map(t => <Option key={t} value={t}>{t}</Option>)}
                  </Select>
                </Descriptions.Item>
                <Descriptions.Item label="优先级">
                  {isClosed ? (
                    <Tag color={priorityInfo.color} style={{ border: 'none' }}>{priorityInfo.label}</Tag>
                  ) : (
                    <Select size="small" value={ticket.priority} style={{ width: 100 }}
                      onChange={handlePriorityChange}>
                      <Option value="low">低</Option>
                      <Option value="normal">普通</Option>
                      <Option value="high">高</Option>
                      <Option value="urgent">紧急</Option>
                    </Select>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="所属App">
                  {ticket.appValue ? (APP_DISPLAY_MAP[ticket.appValue] || ticket.appValue) : <span style={{ color: '#ccc' }}>-</span>}
                </Descriptions.Item>
                <Descriptions.Item label="所属项目">
                  {ticket.projectName || <span style={{ color: '#ccc' }}>-</span>}
                </Descriptions.Item>
                <Descriptions.Item label="创建时间">{ticket.createdAt}</Descriptions.Item>
                <Descriptions.Item label="最近更新">{ticket.updatedAt}</Descriptions.Item>
              </Descriptions>

              <Divider style={{ margin: '12px 0' }} />
              <Title level={5} style={{ marginTop: 0 }}>问题描述</Title>
              <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{ticket.description}</Paragraph>

              {ticket.attachments && ticket.attachments.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <Text strong>附件（{ticket.attachments.length}）：</Text>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
                    {/* 图片类：缩略图，点击可放大预览 */}
                    {ticket.attachments.some(f => f.type === 'image') && (
                      <Image.PreviewGroup>
                        {ticket.attachments.filter(f => f.type === 'image').map((file, idx) => (
                          <Image
                            key={idx}
                            src={file.url}
                            width={80}
                            height={80}
                            style={{ objectFit: 'cover', borderRadius: 6, border: '1px solid #f0f0f0', cursor: 'zoom-in' }}
                          />
                        ))}
                      </Image.PreviewGroup>
                    )}
                    {/* 文件类：图标 + 文件名 + 大小 */}
                    {ticket.attachments.filter(f => f.type !== 'image').map((file, idx) => (
                      <div key={idx}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: 6, background: '#fafafa' }}>
                        <span style={{ fontSize: 20, color: FILE_TYPE_MAP[file.type]?.color || '#8c8c8c', display: 'inline-flex' }}>
                          {FILE_TYPE_MAP[file.type]?.icon || <FileImageOutlined />}
                        </span>
                        <div>
                          <div style={{ fontSize: 13 }}>{file.name}</div>
                          <Text type="secondary" style={{ fontSize: 12 }}>{file.size}</Text>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* TAPD 信息栏（仅领取后显示，位于问题描述与处理进展之间） */}
          {!isPending && (
            <Card bordered={false}
              style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', marginBottom: 16 }}
              title={<span><LinkOutlined style={{ marginRight: 6 }} />TAPD 信息</span>}>
              {ticket.tapd ? (
                <>
                  <Descriptions bordered column={2} size="small">
                    <Descriptions.Item label="TAPD类型">
                      <Tag color={ticket.tapd.type === 'bug' ? 'red' : 'cyan'} style={{ border: 'none' }}>
                        {ticket.tapd.type === 'bug' ? '缺陷' : '需求'}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="状态">
                      <Tag color={TAPD_STATUS_MAP[ticket.tapd.status]?.color || 'default'} style={{ border: 'none' }}>
                        {TAPD_STATUS_MAP[ticket.tapd.status]?.label || ticket.tapd.status}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="标题" span={2}>{ticket.tapd.title}</Descriptions.Item>
                    <Descriptions.Item label="内容" span={2}>
                      {ticket.tapd.content ? (
                        <div
                          style={{ maxHeight: 240, overflow: 'auto', lineHeight: 1.6 }}
                          dangerouslySetInnerHTML={{ __html: ticket.tapd.content }}
                        />
                      ) : (
                        <span style={{ color: '#ccc' }}>-</span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="附件" span={2}>
                      {ticket.tapd.attachments && ticket.tapd.attachments.length > 0 ? (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          {ticket.tapd.attachments.some(f => f.type === 'image') && (
                            <Image.PreviewGroup>
                              {ticket.tapd.attachments.filter(f => f.type === 'image').map((f, idx) => (
                                <Image key={idx} src={f.url} width={64} height={64}
                                  style={{ objectFit: 'cover', borderRadius: 6, border: '1px solid #f0f0f0', cursor: 'zoom-in' }} />
                              ))}
                            </Image.PreviewGroup>
                          )}
                          {ticket.tapd.attachments.filter(f => f.type !== 'image').map((f, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', border: '1px solid #f0f0f0', borderRadius: 6, background: '#fafafa' }}>
                              <span style={{ fontSize: 18, color: FILE_TYPE_MAP[f.type]?.color || '#8c8c8c', display: 'inline-flex' }}>
                                {FILE_TYPE_MAP[f.type]?.icon || <FileOutlined />}
                              </span>
                              <div>
                                <div style={{ fontSize: 12 }}>{f.name}</div>
                                {f.size && <Text type="secondary" style={{ fontSize: 11 }}>{f.size}</Text>}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#ccc' }}>-</span>
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                  <div style={{ marginTop: 12, textAlign: 'right' }}>
                    <Button type="primary" icon={<LinkOutlined />}
                      onClick={() => window.open(buildTapdUrl(ticket.tapd), '_blank')}>
                      {ticket.tapd.type === 'bug' ? '查看缺陷' : '查看需求'}
                    </Button>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <Text type="secondary">该工单尚未创建 TAPD 单，可跳转 TAPD 创建需求/缺陷单</Text>
                  <Button type="primary" icon={<PlusOutlined />} onClick={openTapdDrawer}>
                    创建TAPD
                  </Button>
                </div>
              )}
            </Card>
          )}

          {/* 处理备注卡片 */}
          <Card bordered={false}
            style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', marginBottom: 16 }}
            title="处理进展">
            {remarks.length === 0 ? (
              <Empty description="暂无处理记录" />
            ) : (
              <Timeline
                items={remarks.map(rmk => {
                  const typeInfo = REMARK_TYPE_MAP[rmk.type];
                  return {
                    color: rmk.type === 'system' ? 'gray' : (rmk.type === 'staff' ? 'green' : 'blue'),
                    children: (
                      <div>
                        <div style={{ marginBottom: 4 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>{rmk.createdAt}</Text>
                          <Tag color={typeInfo.color} style={{ border: 'none', marginLeft: 8, fontSize: 11 }}>
                            {typeInfo.label}
                          </Tag>
                          <Text strong style={{ marginLeft: 4 }}>{rmk.author}</Text>
                        </div>
                        <div style={{
                          color: rmk.type === 'system' ? '#999' : '#333',
                          fontSize: rmk.type === 'system' ? 13 : 14,
                          whiteSpace: 'pre-wrap',
                        }}>
                          {rmk.content}
                        </div>
                      </div>
                    ),
                  };
                })}
              />
            )}

            {/* 添加回复 — pending状态不显示 */}
            {!isClosed && !isPending && (
              <div style={{ marginTop: 16, padding: '12px 0', borderTop: '1px solid #f0f0f0' }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>添加回复</Text>
                <TextArea
                  rows={3}
                  value={remarkText}
                  onChange={(e) => setRemarkText(e.target.value)}
                  placeholder="请输入回复内容..."
                  maxLength={2000}
                  showCount
                />
                <div style={{ marginTop: 8, textAlign: 'right' }}>
                  <Button type="primary" icon={<SendOutlined />}
                    loading={savingRemark} onClick={handleAddRemark}>
                    添加回复
                  </Button>
                </div>
              </div>
            )}

            {/* 待处理状态提示 + 领取/分配按钮 */}
            {isPending && (
              <div style={{ marginTop: 16, padding: '12px 0', borderTop: '1px solid #f0f0f0' }}>
                <Alert
                  message="只能先领取工单或分配工单"
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                />
                <Space size={12}>
                  <Button type="primary" onClick={handleClaim}>领取工单</Button>
                  <Button icon={<UserSwitchOutlined />} onClick={handleAssignOpen}>分配工单</Button>
                </Space>
              </div>
            )}
          </Card>

          {/* 操作栏 */}
          {!isClosed && !isPending && (
            <Card bordered={false}
              style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
              <Space size={12}>
                {isProcessing && (
                  <>
                    <Button type="primary" icon={<CheckCircleOutlined />}
                      onClick={handleMarkResolved}>
                      标记已解决
                    </Button>
                    <Button icon={<UndoOutlined />} onClick={handleRelease}>
                      释放工单
                    </Button>
                  </>
                )}
                {isResolved && (
                  <Button type="primary" danger icon={<MinusCircleOutlined />}
                    onClick={handleClose}>
                    关闭工单
                  </Button>
                )}
              </Space>
            </Card>
          )}
        </div>

        {/* ========== 右侧：CRM 客户信息 ========== */}
        <div style={{ width: 300, flexShrink: 0 }}>
          <Card bordered={false}
            style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
            title={<span><TeamOutlined style={{ marginRight: 6 }} />CRM 客户信息</span>}>
            {crm ? (
              <>
                <Descriptions column={1} size="small" colon={false}
                  labelStyle={{ color: '#999', fontSize: 12 }}
                  contentStyle={{ fontSize: 14 }}>
                  <Descriptions.Item label="企业名称">
                    <Text strong>{crm.enterpriseName}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="企业ID">
                    <Text code>{crm.enterpriseId}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="联系人">{crm.contactPerson}</Descriptions.Item>
                  <Descriptions.Item label="联系电话">{crm.contactPhone}</Descriptions.Item>
                  <Descriptions.Item label={
                    <span><TagOutlined style={{ marginRight: 4 }} />责任人</span>
                  }>{crm.responsibleName}</Descriptions.Item>
                </Descriptions>

                <div style={{ marginTop: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                    <TagOutlined style={{ marginRight: 4 }} />客户标签
                  </Text>
                  <Space wrap size={[4, 4]}>
                    {Object.values(crm.tags).flat().map((tag, idx) => (
                      <Tag key={idx} color="purple" style={{ border: 'none' }}>{tag}</Tag>
                    ))}
                  </Space>
                </div>

                {crm.riskNotes && (
                  <div style={{
                    marginTop: 12, padding: 12,
                    background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8,
                  }}>
                    <Text style={{ color: '#fa8c16' }}>
                      <ExclamationCircleOutlined style={{ marginRight: 4 }} />有风险
                    </Text>
                    <div style={{ marginTop: 4, fontSize: 13 }}>{crm.riskNotes}</div>
                  </div>
                )}

                <div style={{ marginTop: 16 }}>
                  <Button type="link" size="small"
                    onClick={() => window.open(`/customer/crm/detail/${crm.enterpriseId}`, '_blank')}>
                    查看完整 CRM 信息 →
                  </Button>
                </div>
              </>
            ) : (
              <Text type="secondary">该企业暂无 CRM 记录</Text>
            )}
          </Card>
        </div>
      </div>

      {/* 分配弹窗 */}
      <Modal
        title="分配工单"
        open={assignModalVisible}
        onOk={handleAssignConfirm}
        onCancel={() => setAssignModalVisible(false)}
        okText="确认分配"
        cancelText="取消"
      >
        <div style={{ marginBottom: 12 }}>
          <Text>工单编号：</Text>
          <Text code>{ticket.ticketNo}</Text>
        </div>
        <Form.Item label="分配至" style={{ marginBottom: 0 }}>
          <Select
            placeholder="请选择客服"
            value={assignTarget}
            onChange={setAssignTarget}
            showSearch
            optionFilterProp="children"
            style={{ width: '100%' }}
          >
            {MOCK_AGENTS.map(agent => (
              <Option key={agent.id} value={agent.id}>
                {agent.name}  {agent.account}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Modal>

      {/* 创建 TAPD 抽屉 */}
      <Drawer
        title="创建 TAPD"
        width={560}
        open={tapdDrawerOpen}
        onClose={() => setTapdDrawerOpen(false)}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => setTapdDrawerOpen(false)}>取消</Button>
            <Button type="primary" onClick={handleCreateTapd}>创建TAPD</Button>
          </Space>
        }
      >
        <Form form={tapdForm} layout="vertical" requiredMark={false}>
          <Form.Item name="space" label="选择空间"
            rules={[{ required: true, message: '请选择空间' }]}>
            <Select placeholder="请选择空间" options={TAPD_SPACES} allowClear />
          </Form.Item>

          <Form.Item name="type" label="选择类型"
            rules={[{ required: true, message: '请选择类型' }]}>
            <Radio.Group options={TAPD_TYPE_OPTIONS} optionType="button" buttonStyle="solid" />
          </Form.Item>

          {/* 需求字段：空间未选择时下方均为空（禁用） */}
          {tapdType === 'story' && (
            <>
              <Form.Item name="storyCategory" label="需求分类">
                <Select placeholder="请选择需求分类" options={TAPD_STORY_CATEGORIES.map(v => ({ value: v, label: v }))} disabled={!tapdSpace} allowClear />
              </Form.Item>
              <Form.Item name="storyPriority" label="需求优先级">
                <Select placeholder="请选择需求优先级" options={TAPD_STORY_PRIORITIES.map(v => ({ value: v, label: v }))} disabled={!tapdSpace} allowClear />
              </Form.Item>
              <Form.Item name="handler" label="处理人">
                <Select placeholder="请选择处理人" options={MOCK_AGENTS.map(a => ({ value: a.name, label: `${a.name}  ${a.account}` }))} disabled={!tapdSpace} allowClear />
              </Form.Item>
              <Form.Item name="creator" label="创建人">
                <Select placeholder="请选择创建人" options={MOCK_AGENTS.map(a => ({ value: a.name, label: `${a.name}  ${a.account}` }))} disabled={!tapdSpace} allowClear />
              </Form.Item>
            </>
          )}

          {/* 缺陷字段：空间未选择时下方均为空（禁用） */}
          {tapdType === 'bug' && (
            <>
              <Form.Item name="bugSource" label="问题来源">
                <Select placeholder="请选择问题来源" options={TAPD_BUG_SOURCES.map(v => ({ value: v, label: v }))} disabled={!tapdSpace} allowClear />
              </Form.Item>
              <Form.Item name="bugPriority" label="优先级">
                <Select placeholder="请选择优先级" options={TAPD_BUG_PRIORITIES.map(v => ({ value: v, label: v }))} disabled={!tapdSpace} allowClear />
              </Form.Item>
              <Form.Item name="bugSeverity" label="严重程度">
                <Select placeholder="请选择严重程度" options={TAPD_BUG_SEVERITIES.map(v => ({ value: v, label: v }))} disabled={!tapdSpace} allowClear />
              </Form.Item>
            </>
          )}

          <Form.Item name="title" label={tapdType === 'bug' ? '缺陷标题' : '需求标题'}
            rules={[{ required: true, message: '请填写标题' }, { max: 200, message: '标题不超过200字' }]}>
            <Input placeholder="默认回显工单标题，可修改" maxLength={200} showCount />
          </Form.Item>

          <Form.Item name="content" label={tapdType === 'bug' ? '缺陷内容' : '需求内容'}
            rules={[{ required: true, message: '请填写内容' }]}>
            <TapdRichEditor attachments={ticket.attachments} onAttachmentsChange={setTapdExtraAttachments} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}

export default TicketDetail;
